'use client';

import { useState, type ReactElement } from 'react';

export interface MirrorLinkItem {
  name: string;
  abbrev: string;
  url: string;
}

interface MirrorLinksGridProps {
  links: MirrorLinkItem[];
  lang: string;
  children: ReactElement[];
}

// ---------------------------------------------------------------------------
// Island geography: university → island + longitude (west-to-east ordering)
// ---------------------------------------------------------------------------

interface IslandEntry { island: string; lng: number }

/** Static mapping of university abbreviation → island + approximate longitude. */
const UNI_ISLAND: Record<string, IslandEntry> = {
  // Sumatra (west to east)
  UTU:    { island: 'Sumatra', lng: 96.13 },  // Meulaboh
  USU:    { island: 'Sumatra', lng: 98.68 },  // Medan
  UNAND:  { island: 'Sumatra', lng: 100.35 }, // Padang
  UNP:    { island: 'Sumatra', lng: 100.35 }, // Padang
  UNRI:   { island: 'Sumatra', lng: 101.45 }, // Pekanbaru
  UNJA:   { island: 'Sumatra', lng: 103.61 }, // Jambi
  UNIB:   { island: 'Sumatra', lng: 102.27 }, // Bengkulu
  UNSRI:  { island: 'Sumatra', lng: 104.76 }, // Palembang
  UNILA:  { island: 'Sumatra', lng: 105.26 }, // Bandar Lampung

  // Java (west to east)
  UI:       { island: 'Jawa', lng: 106.83 },  // Depok
  UNJ:      { island: 'Jawa', lng: 106.87 },  // Jakarta
  UT:       { island: 'Jawa', lng: 106.72 },  // Tangerang Selatan
  UNTIRTA:  { island: 'Jawa', lng: 106.15 },  // Serang
  IPB:      { island: 'Jawa', lng: 106.72 },  // Bogor
  UPI:      { island: 'Jawa', lng: 107.59 },  // Bandung
  ITB:      { island: 'Jawa', lng: 107.61 },  // Bandung
  UNPAD:    { island: 'Jawa', lng: 107.77 },  // Sumedang/Jatinangor
  UNSOED:   { island: 'Jawa', lng: 109.24 },  // Purwokerto
  UNDIP:    { island: 'Jawa', lng: 110.44 },  // Semarang
  UNNES:    { island: 'Jawa', lng: 110.39 },  // Semarang
  UNS:      { island: 'Jawa', lng: 110.86 },  // Surakarta
  UGM:      { island: 'Jawa', lng: 110.37 },  // Yogyakarta
  UNEJ:     { island: 'Jawa', lng: 113.71 },  // Jember
  'UM-ID':  { island: 'Jawa', lng: 112.60 },  // Malang
  UB:       { island: 'Jawa', lng: 112.61 },  // Malang
  UNESA:    { island: 'Jawa', lng: 112.73 },  // Surabaya
  UNAIR:    { island: 'Jawa', lng: 112.78 },  // Surabaya
  ITS:      { island: 'Jawa', lng: 112.79 },  // Surabaya
  UPNJATIM: { island: 'Jawa', lng: 112.77 },  // Surabaya

  // Bali & Nusa Tenggara
  UNDIKSHA: { island: 'Bali & Nusa Tenggara', lng: 115.09 }, // Singaraja, Bali
  UNRAM:    { island: 'Bali & Nusa Tenggara', lng: 116.13 }, // Mataram, Lombok
  UNDANA:   { island: 'Bali & Nusa Tenggara', lng: 123.59 }, // Kupang, Timor

  // Kalimantan
  UNTAN:    { island: 'Kalimantan', lng: 109.34 }, // Pontianak
  ULM:      { island: 'Kalimantan', lng: 114.59 }, // Banjarmasin
  UNMUL:    { island: 'Kalimantan', lng: 117.15 }, // Samarinda
  ITK:      { island: 'Kalimantan', lng: 116.85 }, // Balikpapan

  // Sulawesi
  UNG:      { island: 'Sulawesi', lng: 123.06 }, // Gorontalo
  UNM:      { island: 'Sulawesi', lng: 119.49 }, // Makassar
  UNHAS:    { island: 'Sulawesi', lng: 119.49 }, // Makassar
  UHO:      { island: 'Sulawesi', lng: 122.51 }, // Kendari

  // Maluku & Papua
  UNKHAIR:  { island: 'Maluku & Papua', lng: 127.33 }, // Ternate

  // Other / Bandung arts
  ISBI:     { island: 'Jawa', lng: 107.62 }, // Bandung
};

/** Island display order (west to east). */
const ISLAND_ORDER = [
  'Sumatra',
  'Jawa',
  'Bali & Nusa Tenggara',
  'Kalimantan',
  'Sulawesi',
  'Maluku & Papua',
];

function getIslandGroup(abbrev: string): IslandEntry {
  return UNI_ISLAND[abbrev] ?? { island: 'Lainnya', lng: 999 };
}

/**
 * Client wrapper that adds a search filter to the mirror links grid.
 * Cards are server-rendered and passed as children with matching keys.
 */
/** Inline logo that falls back to a styled first-letter badge on error. */
export function MirrorLogo({ abbrev, src }: { abbrev: string; src: string | null }) {
  const [error, setError] = useState(false);

  if (!src || error) {
    return (
      <span
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent-primary/10 text-sm font-bold text-accent-primary"
        aria-hidden="true"
      >
        {abbrev.charAt(0)}
      </span>
    );
  }

  return (
    <img
      src={src}
      alt={`${abbrev} logo`}
      width={32}
      height={32}
      loading="lazy"
      className="h-8 w-8 shrink-0"
      onError={() => setError(true)}
    />
  );
}

export function MirrorLinksGrid({ links, lang, children }: MirrorLinksGridProps) {
  const [query, setQuery] = useState('');
  const isId = lang === 'id';

  const lowerQ = query.toLowerCase().trim();
  const matchingAbbrevs = lowerQ
    ? new Set(
        links
          .filter(l => l.abbrev.toLowerCase().includes(lowerQ) || l.name.toLowerCase().includes(lowerQ))
          .map(l => l.abbrev),
      )
    : null;

  // Map children to links by index (children are produced from links.map in the same order)
  const childByAbbrev = new Map<string, ReactElement>();
  for (let i = 0; i < links.length && i < children.length; i++) {
    childByAbbrev.set(links[i].abbrev, children[i]);
  }

  // Build sorted index: island order first, then west-to-east within each island
  const sortedLinks = [...links].sort((a, b) => {
    const ga = getIslandGroup(a.abbrev);
    const gb = getIslandGroup(b.abbrev);
    const ia = ISLAND_ORDER.indexOf(ga.island);
    const ib = ISLAND_ORDER.indexOf(gb.island);
    if (ia !== ib) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    return ga.lng - gb.lng;
  });

  const visibleLinks = matchingAbbrevs === null
    ? sortedLinks
    : sortedLinks.filter(l => matchingAbbrevs.has(l.abbrev));

  // Group into island sections
  const grouped = new Map<string, ReactElement[]>();
  for (const link of visibleLinks) {
    const { island } = getIslandGroup(link.abbrev);
    const child = childByAbbrev.get(link.abbrev);
    if (!child) continue;
    if (!grouped.has(island)) grouped.set(island, []);
    grouped.get(island)!.push(child);
  }

  const orderedIslands = [...grouped.keys()];

  return (
    <>
      <div className="relative mt-4">
        <svg
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-secondary/50"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
        </svg>
        <input
          type="search"
          placeholder={isId ? 'Cari universitas...' : 'Search university...'}
          value={query}
          onChange={e => setQuery(e.target.value)}
          className="w-full rounded-lg border border-border-subtle bg-surface-root py-2.5 pl-9 pr-3 text-sm text-text-primary placeholder:text-text-secondary/50 focus:border-accent-primary focus:outline-none focus:ring-1 focus:ring-accent-primary"
        />
      </div>

      <div
        aria-label={isId ? 'Link mirror universitas' : 'University mirror links'}
      >
        {orderedIslands.map(island => (
          <div key={island} className="mt-5">
            <h4 className="mb-3 text-xs font-bold uppercase tracking-wider text-text-secondary">
              {island}
            </h4>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" role="list">
              {grouped.get(island)}
            </div>
          </div>
        ))}
      </div>

      {matchingAbbrevs !== null && visibleLinks.length === 0 && (
        <p className="mt-3 text-center text-sm text-text-secondary">
          {isId ? 'Tidak ada universitas yang cocok.' : 'No matching universities.'}
        </p>
      )}
    </>
  );
}
